import '../../i18n/autoTranslate';
import React, { useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { addDays, format, isSameDay, isSameMinute } from "date-fns";
import { Badge } from "../../../components/ui/shadcn/badge";

const HOURS = Array.from({ length: 14 }).map((_, i) => 7 + i);

const buildSlot = (day, hour) => {
  const d = new Date(day);
  d.setHours(hour, 0, 0, 0);
  return d;
};

export default function WeekView({
  weekStart,
  weekDays,
  sessions,
  loading,
  selectedSlot,
  onSlotClick,
  onSlotHover,
  dayOnly = false
}) {
  const days = useMemo(() => {
    if (dayOnly) return weekDays;
    return weekDays || Array.from({ length: 7 }).map((_, i) => addDays(weekStart, i));
  }, [dayOnly, weekDays, weekStart]);

  const sessionsByDay = useMemo(() => {
    const map = new Map();
    for (const d of days) map.set(format(d, "yyyy-MM-dd"), []);
    for (const s of sessions || []) {
      if (!s?.scheduled_time) continue;
      const dt = new Date(s.scheduled_time);
      const key = format(dt, "yyyy-MM-dd");
      if (!map.has(key)) continue;
      map.get(key).push(s);
    }
    for (const [k, list] of map.entries()) {
      map.set(
        k,
        list.sort((a, b) => new Date(a.scheduled_time) - new Date(b.scheduled_time))
      );
    }
    return map;
  }, [days, sessions]);

  return (
    <div className="rounded-3xl border border-white/10 bg-white/5 overflow-hidden">
      <div className="grid" style={{ gridTemplateColumns: `72px repeat(${days.length}, minmax(0, 1fr))` }}>
        <div className="h-14 border-b border-white/10 bg-[#0B0B12]/30" />
        {days.map((day) => {
          const isToday = isSameDay(day, new Date());
          return (
            <div key={format(day, "yyyy-MM-dd")} className="h-14 border-b border-white/10 bg-[#0B0B12]/30 px-3 flex items-center justify-between">
              <div className="min-w-0">
                <div className={`text-xs ${isToday ? "text-indigo-200" : "text-white/60"}`}>{format(day, "EEE")}</div>
                <div className={`text-sm font-semibold truncate ${isToday ? "text-white" : "text-white/85"}`}>{format(day, "MMM d")}</div>
              </div>
              {isToday && (
                <Badge variant="outline" className="bg-white/5 border-white/10 text-white/70">
                  Today
                </Badge>
              )}
            </div>
          );
        })}
      </div>

      <div className="max-h-[62vh] overflow-auto">
        <div className="grid" style={{ gridTemplateColumns: `72px repeat(${days.length}, minmax(0, 1fr))` }}>
          {HOURS.map((hour) => (
            <React.Fragment key={hour}>
              <div className="h-14 border-b border-white/10 bg-[#0B0B12]/20 px-3 flex items-start pt-3">
                <div className="text-xs text-white/50">{String(hour).padStart(2, "0")}:00</div>
              </div>
              {days.map((day) => {
                const slot = buildSlot(day, hour);
                const key = `${format(day, "yyyy-MM-dd")}-${hour}`;
                const dayKey = format(day, "yyyy-MM-dd");
                const daySessions = sessionsByDay.get(dayKey) || [];
                const slotSessions = daySessions.filter((s) => {
                  const dt = new Date(s.scheduled_time);
                  return dt.getHours() === hour;
                });
                const active = Boolean(selectedSlot && isSameMinute(slot, selectedSlot));

                return (
                  <motion.button
                    key={key}
                    type="button"
                    onMouseEnter={() => onSlotHover && onSlotHover(slot)}
                    onClick={() => onSlotClick && onSlotClick(slot)}
                    className={`relative h-14 border-b border-white/10 border-l border-white/10 text-left px-2 py-2 transition-colors ${
                      active ? "bg-indigo-500/10" : "bg-transparent hover:bg-white/5"
                    }`}
                    whileHover={{ scale: 1.004 }}
                    whileTap={{ scale: 0.995 }}
                  >
                    <AnimatePresence initial={false}>
                      {!loading && slotSessions.slice(0, 2).map((s) => (
                        <motion.div
                          key={s.id}
                          initial={{ opacity: 0, y: 6 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 6 }}
                          className="rounded-xl border border-white/10 bg-gradient-to-r from-indigo-600/25 to-purple-600/20 px-2 py-1 mb-1"
                        >
                          <div className="text-xs font-semibold text-white truncate">{s.title}</div>
                          <div className="text-[10px] text-white/60 truncate">{s.company} • {s.duration}m</div>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                    {loading && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="h-4 w-4 rounded-full border-2 border-white/20 border-t-white/70 animate-spin" />
                      </div>
                    )}
                  </motion.button>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

